import React, { useMemo } from 'react'
import { Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import { useSelector } from 'react-redux'

// import { Control, useController, useWatch } from 'react-hook-form'
import TestIds from '../../../testIds'
import { SeedMix, SeedProductLineSchema, SeedTypesenseProduct } from '../types/SeedOrderFormType'
import Checkbox from '../../../components/Checkbox'
import { selectCurrentFarmerAccount } from '../../azure-b2c/storage'
import { SideContainer } from '../../products/components/ProductCard'
import { Product } from '../../products/storage'
import { selectProduct } from '../../products/storage/stocks.slice'
import { CartItem } from '../../cart/storage/cart.types'
import { CartItemCard } from '../../products/components/ProductCard/CartItemCard'
import { SeedCounter } from '../components/SeedCounter'
import _ from 'lodash'
import {
  adjustQuantityAfterSowingRate,
  adjustSowingRateAfterQuantity,
  roundedTo,
} from '../utils/calculateTotal'
import { useAppDispatch } from '../../../store/rootStore'
import { setSeedValue } from '../storage/seedCart.slice'

export const ProductRow = ({
  // control,
  index,
  remove,
  seedMix,
}: {
  // control: Control<SeedOrdersInfo>
  index: number
  remove: (index: number) => void
  seedMix: SeedMix
}) => {
  const theme = useTheme()
  const isSm = useMediaQuery(theme.breakpoints.down('sm'))
  const account = useSelector(selectCurrentFarmerAccount)
  const dispatch = useAppDispatch()
  const isTfo = account?.hasTfoAccess || false

  const product = seedMix.products[index]

  const item = useSelector(selectProduct(product.productId)) as Product

  const unit = useMemo(() => {
    if (item && (item as unknown as SeedTypesenseProduct).unit)
      return (item as unknown as SeedTypesenseProduct).unit
    return 'kg'
  }, [item])

  const sowingRateUnit = useMemo(() => `${unit}/Ha`, [unit])

  const quantityField = seedMix.products[index].quantity
  const sowingRateField = seedMix.products[index].sowingRate
  const showQuantityWarning = seedMix.products[index].quantityRoundedUp
  const treatmentField = seedMix.products[index].treatment

  const validation = SeedProductLineSchema.safeParse(seedMix.products[index])
  const sowingRateError = validation.error?.formErrors?.fieldErrors?.sowingRate?.[0]
  const quantityError = validation.error?.formErrors?.fieldErrors?.quantity?.[0]

  const onRemove = () => {
    remove(index)
  }

  const renderQuickOrderButton = ({ direction = 'column' }: { direction?: 'row' | 'column' }) => (
    <Stack pt='24px' alignItems={isSm ? 'flex-start' : 'flex-end'}>
      {showQuantityWarning ? (
        <Typography
          variant='body2'
          color={theme.colors.error700}
          mt={0.5}
          data-testid={TestIds.feat.seed.paddock.dateInputError}
        >
          Quantity rounded up to whole number
        </Typography>
      ) : null}
      <Stack direction={direction} justifyContent='flex-end' spacing={2}>
        {product.isMixingFee ? null : (
          <SeedCounter
            errorMessage={sowingRateError}
            label='Sowing rate'
            onChange={(sowingRate) => {
              const accountNumber = account?.accountNumber
              if (accountNumber) {
                let value = sowingRate
                if (!value || value < 0) {
                  value = 0
                }

                let copy = _.cloneDeep(seedMix)
                copy.products[index].sowingRate = roundedTo(value)

                if (value !== 0) {
                  copy = adjustQuantityAfterSowingRate(copy)

                  dispatch(
                    setSeedValue({
                      accountNumber,
                      key: ``,
                      value: copy,
                      seedMix,
                    }),
                  )
                } else {
                  dispatch(
                    setSeedValue({
                      accountNumber,
                      key: `products[${index}].sowingRate`,
                      value: value,
                      seedMix,
                    }),
                  )
                }
              }
            }}
            type='decimal'
            testId={TestIds.feat.seed.product.sowingRate}
            unit={sowingRateUnit}
            value={sowingRateField}
          />
        )}
        <SeedCounter
          disabled={!!product.isMixingFee}
          errorMessage={quantityError}
          label='Quantity'
          onChange={(quantity) => {
            const accountNumber = account?.accountNumber
            if (accountNumber) {
              let value = quantity
              if (!value || value < 1) {
                value = 0
              }

              let copy = _.cloneDeep(seedMix)
              copy.products[index].quantity = value

              if (value !== 0) {
                copy = adjustSowingRateAfterQuantity(copy)

                dispatch(
                  setSeedValue({
                    accountNumber,
                    key: ``,
                    value: copy,
                    seedMix,
                  }),
                )
              } else {
                dispatch(
                  setSeedValue({
                    accountNumber,
                    key: `products[${index}].quantity`,
                    value: value,
                    seedMix,
                  }),
                )
              }
            }
          }}
          testId={TestIds.feat.seed.product.quantity}
          unit={unit}
          value={quantityField}
        />
      </Stack>
      <Stack>
        {isTfo && !product.isMixingFee ? (
          <Checkbox
            label='Farmlands to Poncho treat'
            checked={treatmentField}
            onChange={(_, checked) => {
              const accountNumber = account?.accountNumber
              if (accountNumber) {
                dispatch(
                  setSeedValue({
                    accountNumber,
                    key: `products[${index}].treatment`,
                    value: checked,
                    seedMix,
                  }),
                )
              }
            }}
            sx={{ textWrap: 'nowrap' }}
          />
        ) : null}
      </Stack>
    </Stack>
  )

  const renderSideContainer = useMemo(() => {
    if (!isSm) {
      return (
        <SideContainer sx={{ justifyContent: 'flex-end' }}>
          {renderQuickOrderButton({ direction: 'row' })}
        </SideContainer>
      )
    }
    return undefined
  }, [isSm, renderQuickOrderButton])

  return (
    <CartItemCard
      item={{ ...item, ...product } as CartItem}
      renderSideContainer={renderSideContainer}
      renderQuickOrderButton={renderQuickOrderButton({})}
      priceEditable={false}
      onRemove={product.isMixingFee ? undefined : onRemove}
      enableVariants={true}
      showUrgentDeliveryUnavailableMessage={false}
      showImage={false}
    />
  )
}
